import { useMemo, useState } from "react";
import { Grid, Input, Button, Checkbox, Dropdown, Icon, Popup } from "semantic-ui-react";
import { Formik, Form } from "formik";
import * as yup from "yup";
import { ErrorInfo } from "components/form";
import { toast } from "components/Toast/Toast";
import { validateResolution } from "services/common";
import ConfirmationModal from "components/ConfirmationModal/ConfirmationModal";
import _ from "lodash";
import { getRequiredResolution } from "services/apiMiddleware";

const RESOLUTION_TYPES = {
  IMAGE: "IMAGE",
  IMAGE_AND_VIDEO: "IMAGE_AND_VIDEO",
};

const values = [0, 8, 16, 24, 32, 36, 56, 60, 64, 68, 92, 96, 100, 108, 116, 120, 144, 192];

const cutOptions = values.map((value) => ({
  key: value.toString(),
  text: value.toString(),
  value: value.toString(),
}));

export const ResolutionForm = ({
  requiredResolutions,
  nativeResolutions,
  resolutionToUpdate,
  setUpdatingRes,
  dispatch,
  t,
}) => {
  const [confirmationModalOpen, setConfirmationModalOpen] = useState(false);
  const [confirmationModalData, setConfirmationModalData] = useState({
    text: "",
    headerText: "",
    icon: "",
  });

  const INCORRECT_RESOLUTION_ERROR = t("incorrect_resolution_error");

  const resolutionSchema = yup.object().shape({
    required_resolution: yup
      .string()
      .required(t("required"))
      .test("is-resolution", INCORRECT_RESOLUTION_ERROR, validateResolution),
    native_resolution: yup
      .string()
      .required(t("required"))
      .test("is-resolution", INCORRECT_RESOLUTION_ERROR, validateResolution),
  });

  const initialValues = useMemo(
    () =>
      !resolutionToUpdate
        ? {
            required_resolution: "",
            native_resolution: "",
            isMp4Checked: false,
            horizontalCut: "0",
            verticalCut: "0",
          }
        : {
            required_resolution: resolutionToUpdate.requiredResolution.value,
            native_resolution: resolutionToUpdate.value,
            isMp4Checked: resolutionToUpdate.type === RESOLUTION_TYPES.IMAGE ? false : true,
            active: resolutionToUpdate.active,
            horizontalCut: resolutionToUpdate.horizontalCut,
            verticalCut: resolutionToUpdate.verticalCut,
          },
    [resolutionToUpdate]
  );

  const getNativeResolutionsToSearch = () => {
    // If updating, don't include the to be updated resolution
    if (resolutionToUpdate) {
      return _.filter(nativeResolutions, function (native_resolution) {
        return !(
          native_resolution.value === resolutionToUpdate.value &&
          native_resolution.type === resolutionToUpdate.type
        );
      });
      // If creating, include all native resolutions
    } else return nativeResolutions;
  };

  const nativeResValAndTypeExists = (val, type) => {
    return !!getNativeResolutionsToSearch().find(
      (native_resolution) => native_resolution.value === val && native_resolution.type === type
    );
  };

  const getCorrectRequiredResForNativeRes = (nativeVal, nativeType, requiredVal) => {
    const otherType =
      nativeType === RESOLUTION_TYPES.IMAGE
        ? RESOLUTION_TYPES.IMAGE_AND_VIDEO
        : RESOLUTION_TYPES.IMAGE;

    const otherNativeRes = getNativeResolutionsToSearch().find(
      (native_resolution) =>
        native_resolution.value === nativeVal && otherType === native_resolution.type
    );

    // Native res with the other type doesn't exist -> any required resolution value is allowed
    if (!otherNativeRes) return requiredVal;

    return otherNativeRes.requiredResolution.value;
  };

  const nativeResCreationOrUpdateAllowed = (nativeVal, nativeType, requiredVal) => {
    if (nativeResValAndTypeExists(nativeVal, nativeType)) {
      toast("warning", t("native_resolution_exists_error"));
      return false;
    }

    const correctRequiredVal = getCorrectRequiredResForNativeRes(
      nativeVal,
      nativeType,
      requiredVal
    );

    // If there exists a native resolution with the other format,
    //  the native resolution to be created/updated has to use the same required resolution value
    if (requiredVal !== correctRequiredVal) {
      toast(
        "warning",
        t("incorrect_required_resolution_value_error", { required_res_value: correctRequiredVal })
      );
      return false;
    }
    return true;
  };

  const submitCreate = async (values, actions) => {
    try {
      const type = values.isMp4Checked ? RESOLUTION_TYPES.IMAGE_AND_VIDEO : RESOLUTION_TYPES.IMAGE;

      if (
        !nativeResCreationOrUpdateAllowed(
          values.native_resolution,
          type,
          values.required_resolution
        )
      )
        return;

      const createRequiredProps = {
        value: values.required_resolution,
        type,
      };

      const requiredResolution = await dispatch.requiredResolutions.create(createRequiredProps);

      const createNativeProps = {
        value: values.native_resolution,
        requiredResolutionID: requiredResolution.id,
        type,
        active: true, // New native resolution is always active
        horizontalCut: values.horizontalCut,
        verticalCut: values.verticalCut,
      };

      // removed scale option

      await dispatch.nativeResolutions.create(createNativeProps);
      toast(undefined, t("digital_size_creation_success"));
    } catch (error) {
      console.error(error);
      toast("error", t("digital_size_creation_failure"));
    }
  };

  const submitUpdate = async (values, actions) => {
    const originalTypeIsImageAndVideo =
      resolutionToUpdate.type === RESOLUTION_TYPES.IMAGE_AND_VIDEO;
    // Do nothing if nothing was changed
    if (
      values.required_resolution === resolutionToUpdate.requiredResolution.value &&
      values.native_resolution === resolutionToUpdate.value &&
      values.isMp4Checked === originalTypeIsImageAndVideo &&
      values.active === resolutionToUpdate.active &&
      values.horizontalCut === resolutionToUpdate.horizontalCut &&
      values.verticalCut === resolutionToUpdate.verticalCut
    ) {
      toast(undefined, t("digital_size_nothing_updated"));
      setUpdatingRes(false);
      return;
    }

    try {
      const type = values.isMp4Checked ? RESOLUTION_TYPES.IMAGE_AND_VIDEO : RESOLUTION_TYPES.IMAGE;
      let requiredResolutionID = resolutionToUpdate.requiredResolution.id;

      if (
        !nativeResCreationOrUpdateAllowed(
          values.native_resolution,
          type,
          values.required_resolution
        )
      )
        return;

      // Required resolution or type has changed -> create or get the new required resolution with correct type
      if (
        values.required_resolution !== resolutionToUpdate.requiredResolution.value ||
        values.isMp4Checked !== originalTypeIsImageAndVideo
      ) {
        const createRequiredProps = {
          value: values.required_resolution,
          type: values.isMp4Checked ? RESOLUTION_TYPES.IMAGE_AND_VIDEO : RESOLUTION_TYPES.IMAGE,
        };

        const requiredResolution = await dispatch.requiredResolutions.create(createRequiredProps);
        requiredResolutionID = requiredResolution.id;
      }

      const updateNativeProps = {
        id: resolutionToUpdate.id,
        value: values.native_resolution,
        requiredResolutionID,
        type,
        active: values.active,
        horizontalCut: values.horizontalCut,
        verticalCut: values.verticalCut,
      };

      await dispatch.nativeResolutions.update(updateNativeProps);

      // Delete required resolution if it is now not used by any native resolution
      const { data } = await getRequiredResolution(resolutionToUpdate.requiredResolution.id);
      if (data && data.nativeResolutions.items.length === 0) {
        await dispatch.requiredResolutions.delete(data.id);
      }

      toast(undefined, t("digital_size_update_success"));
    } catch (error) {
      console.error(error);
      toast("error", t("digital_size_update_failure"));
    }
    setUpdatingRes(false);
  };

  const RequiredResolutionDataListOptions = () => {
    if (!requiredResolutions) return null;
    return requiredResolutions.map((res, i) => {
      return (
        <option key={i}>
          {res.value + " " + (res.type === RESOLUTION_TYPES.IMAGE ? "jpg/png" : "mp4/mov/jpg/png")}
        </option>
      );
    });
  };

  return (
    <Formik
      validationSchema={resolutionSchema}
      initialValues={initialValues}
      onSubmit={
        resolutionToUpdate
          ? /** Clicking update button triggers validation and then opens confirmation modal. Submitting happens after clicking ok confirmation modal */
            () => null
          : submitCreate
      }
    >
      {({ values, handleChange, errors, isSubmitting, setFieldValue, submitCount }) => (
        <Form>
          <h3>{resolutionToUpdate ? t("digital_size_update") : t("digital_size_create")}:</h3>
          <Grid columns={4} verticalAlign="middle" divided>
            <Grid.Row className="form-field-row">
              <Grid.Column width={3}>
                <Input
                  placeholder={t("required_resolution")}
                  name="required_resolution"
                  value={values.required_resolution}
                  onChange={(_e, { name, value }) => {
                    // TODO fix required_resolution showing an error after selecting an option for the first time without writing anything to the field
                    //  (after selecting an option, the value stays empty for some reason until something else is done in the form which refreshes it)
                    const valueSplit = value.split(" ");
                    setFieldValue(name, valueSplit[0]);
                    if (valueSplit.length === 2) {
                      if (valueSplit[1] === "jpg/png") setFieldValue("isMp4Checked", false);
                      else if (valueSplit[1] === "mp4/mov/jpg/png")
                        setFieldValue("isMp4Checked", true);
                    }
                  }}
                  error={!!errors.required_resolution}
                  fluid
                  list="requiredResolutions"
                  autoComplete="off"
                />
                <datalist id="requiredResolutions">
                  <RequiredResolutionDataListOptions />
                </datalist>
              </Grid.Column>
              <Grid.Column width={3}>
                <Input
                  placeholder={t("native_resolution")}
                  name="native_resolution"
                  value={values.native_resolution}
                  // onChange={handleChange}
                  onChange={(_e, { name, value }) => {
                    // TODO fix required_resolution showing an error after selecting an option for the first time without writing anything to the field
                    //  (after selecting an option, the value stays empty for some reason until something else is done in the form which refreshes it)
                    setFieldValue(name, value);
                  }}
                  error={!!errors.native_resolution}
                  fluid
                />
              </Grid.Column>
              <Grid.Column style={{ maxWidth: "110px" }}>
                <Checkbox label="jpg/png" defaultChecked disabled />
                <Checkbox
                  name="mp4/mov"
                  label="mp4/mov"
                  onChange={(_e, { checked }) => {
                    setFieldValue("isMp4Checked", checked);
                  }}
                  checked={values.isMp4Checked}
                />

                {resolutionToUpdate && (
                  <Checkbox
                    name="active"
                    label={t("active")}
                    onChange={(_e, { checked }) => {
                      setFieldValue("active", checked);
                    }}
                    checked={values.active}
                  />
                )}
              </Grid.Column>
              <Grid.Column width={3}>
                <Popup
                  content={t("horizontal_cut_info")}
                  trigger={
                    <p style={{ marginBottom: "0px" }}>
                      {t("horizontal_cut")} <Icon name="info circle" />
                    </p>
                  }
                />
                <Dropdown
                  selection
                  name="horizontalCut"
                  options={cutOptions}
                  onChange={(_e, { name, value }) => {
                    setFieldValue(name, value);
                  }}
                  value={values.horizontalCut}
                />
              </Grid.Column>
              <Grid.Column width={3}>
                <Popup
                  content={t("vertical_cut_info")}
                  trigger={
                    <p style={{ marginBottom: "0px" }}>
                      {t("vertical_cut")} <Icon name="info circle" />
                    </p>
                  }
                />
                <Dropdown
                  selection
                  name="verticalCut"
                  options={cutOptions}
                  onChange={(_e, { name, value }) => {
                    setFieldValue(name, value);
                  }}
                  value={values.verticalCut}
                />
              </Grid.Column>
              <Grid.Column width={2}>
                {!resolutionToUpdate && (
                  <Button primary type="submit" loading={isSubmitting} fluid>
                    {t("create")}
                  </Button>
                )}
                {resolutionToUpdate && (
                  <div>
                    <Button
                      primary
                      type="submit"
                      loading={isSubmitting}
                      fluid
                      onClick={() => {
                        const noErrors = Object.keys(errors).length === 0;
                        if (noErrors) {
                          setConfirmationModalData({
                            text: t("update_digital_size_modal_text"),
                            headerText: t("digital_size_update"),
                            icon: "edit",
                          });
                          setConfirmationModalOpen(true);
                        }
                      }}
                    >
                      {t("update")}
                    </Button>
                    <Button
                      basic
                      type="button"
                      onClick={() => setUpdatingRes(false)}
                      fluid
                      style={{ marginTop: "5px" }}
                    >
                      {t("cancel")}
                    </Button>
                  </div>
                )}
              </Grid.Column>
            </Grid.Row>
          </Grid>
          <ConfirmationModal
            data={confirmationModalData}
            open={confirmationModalOpen}
            setOpen={setConfirmationModalOpen}
            onSuccess={submitUpdate}
            dataForOnSuccess={values}
          />
          <ErrorInfo errors={errors} submitCount={submitCount} t={t} tPrefix={""} />
        </Form>
      )}
    </Formik>
  );
};
